import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ChevronRight } from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';

export default function ConciliacionForm({ initialData, onSubmit }) {
  const [formData, setFormData] = useState({
    cuenta_bancaria: initialData?.cuenta_bancaria || 'principal',
    periodo_inicio: initialData?.periodo_inicio || new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    periodo_fin: initialData?.periodo_fin || new Date().toISOString().split('T')[0],
    num_movimientos_aprox: initialData?.num_movimientos_aprox || '',
    observaciones: initialData?.observaciones || '',
  });
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.cuenta_bancaria) newErrors.cuenta_bancaria = 'Requerido';
    if (!formData.periodo_inicio) newErrors.periodo_inicio = 'Requerido';
    if (!formData.periodo_fin) newErrors.periodo_fin = 'Requerido';
    if (formData.periodo_inicio > formData.periodo_fin) {
      newErrors.periodo_fin = 'Debe ser posterior a la fecha de inicio';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Alert className="bg-cyan-50 border-cyan-200">
        <AlertDescription className="text-cyan-800">
          La conciliación bancaria compara los movimientos de tu cuenta con las facturas emitidas y gastos registrados,
          identificando discrepancias y movimientos sin justificar.
        </AlertDescription>
      </Alert>

      <div className="space-y-4">
        <div>
          <Label>Cuenta Bancaria *</Label>
          <Select value={formData.cuenta_bancaria} onValueChange={(v) => handleChange('cuenta_bancaria', v)}>
            <SelectTrigger data-testid="select-cuenta">
              <SelectValue placeholder="Selecciona una cuenta" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="principal">Cuenta Principal</SelectItem>
              <SelectItem value="secundaria">Cuenta Secundaria</SelectItem>
              <SelectItem value="otra">Otra Cuenta</SelectItem>
            </SelectContent>
          </Select>
          {errors.cuenta_bancaria && <p className="text-red-500 text-xs mt-1">{errors.cuenta_bancaria}</p>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Fecha de Inicio *</Label>
            <Input
              type="date"
              value={formData.periodo_inicio}
              onChange={(e) => handleChange('periodo_inicio', e.target.value)}
              className={errors.periodo_inicio ? 'border-red-500' : ''}
              data-testid="input-periodo-inicio"
            />
            {errors.periodo_inicio && <p className="text-red-500 text-xs mt-1">{errors.periodo_inicio}</p>}
          </div>
          <div>
            <Label>Fecha de Fin *</Label>
            <Input
              type="date"
              value={formData.periodo_fin}
              onChange={(e) => handleChange('periodo_fin', e.target.value)}
              className={errors.periodo_fin ? 'border-red-500' : ''}
              data-testid="input-periodo-fin"
            />
            {errors.periodo_fin && <p className="text-red-500 text-xs mt-1">{errors.periodo_fin}</p>}
          </div>
        </div>

        <div>
          <Label>Número Aproximado de Movimientos</Label>
          <Input
            type="number"
            min="0"
            value={formData.num_movimientos_aprox}
            onChange={(e) => handleChange('num_movimientos_aprox', e.target.value)}
            placeholder="Opcional"
          />
        </div>

        <div>
          <Label>Observaciones</Label>
          <Textarea
            value={formData.observaciones}
            onChange={(e) => handleChange('observaciones', e.target.value)}
            placeholder="Información adicional relevante para la conciliación..."
            rows={3}
          />
        </div>
      </div>

      <Button type="submit" className="w-full" data-testid="btn-submit-form">
        Continuar <ChevronRight className="w-4 h-4 ml-2" />
      </Button>
    </form>
  );
}
